import { useCallback, useState, type ReactNode } from 'react';
import { usePmsReach, type PmsBundle } from './PmsProvider.js';
import { createTargetDecision } from './logic.js';

// « Dans quel établissement ? » — asked when a form creates something with no
// anchor (U2, PMS-UNIFIED-DESK-BRIEF §4, UD2): a new room, a season calendar, a
// rate profile, a product. Nothing the form acts on names an établissement, so
// the user does — at the moment of creating, with NO default. A default would
// be the screen deciding again, which is what UD2 exists to stop.
//
// Asked at the moment of creation rather than held in a page-wide select: a
// select that stays on the page is a second switcher, the very thing M11′
// removes. With one établissement in reach nothing is asked — the answer is
// already known.

/** The question's panel, in the style of the fiche it precedes. */
function CreateTargetPicker({
  reach,
  title,
  onPick,
  onClose,
}: {
  reach: readonly PmsBundle[];
  title: string;
  onPick: (bundle: PmsBundle) => void;
  onClose: () => void;
}) {
  return (
    <aside className="pms-sheet" aria-label={title}>
      <header className="pms-sheet-head">
        <h3>{title}</h3>
        <button type="button" className="pms-sheet-close" onClick={onClose} aria-label="Fermer">
          ✕
        </button>
      </header>
      <div className="pms-sheet-body">
        <p>Dans quel établissement ?</p>
        <div className="pms-chips" role="group" aria-label="Établissement">
          {reach.map((b) => (
            <button key={b.property.code} type="button" className="pms-chip" onClick={() => onPick(b)}>
              {b.property.labelFr}
            </button>
          ))}
        </div>
      </div>
    </aside>
  );
}

export interface AskEtablissement {
  /** Resolves with the établissement to create in, or `null` if the user
   *  closed the question (or holds none). Immediate when there is one. */
  ask(title: string): Promise<PmsBundle | null>;
  /** Render this where the panel should appear; `null` while nothing is asked. */
  picker: ReactNode;
}

export function useAskEtablissement(): AskEtablissement {
  const reach = usePmsReach();
  const [pending, setPending] = useState<{
    title: string;
    resolve: (b: PmsBundle | null) => void;
  } | null>(null);

  const ask = useCallback(
    (title: string): Promise<PmsBundle | null> => {
      const decision = createTargetDecision(reach);
      if (decision.kind === 'none') return Promise.resolve(null);
      if (decision.kind === 'only') return Promise.resolve(decision.item);
      return new Promise((resolve) => setPending({ title, resolve }));
    },
    [reach],
  );

  const picker = pending ? (
    <CreateTargetPicker
      reach={reach}
      title={pending.title}
      onPick={(b) => {
        pending.resolve(b);
        setPending(null);
      }}
      onClose={() => {
        pending.resolve(null);
        setPending(null);
      }}
    />
  ) : null;

  return { ask, picker };
}

/** What to tell the user after creating somewhere the screen is not showing —
 *  the thing exists, it is just in the other building. */
export function createdElsewhereFr(bundle: PmsBundle, current: string | null): string | null {
  return bundle.property.code === current
    ? null
    : `Créé dans « ${bundle.property.labelFr} » — choisissez cet établissement pour le voir.`;
}
